import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import "@mdi/font/css/materialdesignicons.css";

// Material Design 3 compliant color palette
const lightTheme = {
  dark: false,
  colors: {
    // Material Design 3 primary color palette
    primary: "#004F93",
    "on-primary": "#FFFFFF",
    "primary-container": "#D7EBFC",
    "on-primary-container": "#001D32",
    
    secondary: "#FFDA1A",
    "on-secondary": "#000000",
    "secondary-container": "#FFF4C4",
    "on-secondary-container": "#2D1B00",
    
    tertiary: "#e91e63",
    "on-tertiary": "#FFFFFF",
    "tertiary-container": "#F8BBD0",
    "on-tertiary-container": "#3F0020",
    
    // System colors
    error: "#E00751",
    "on-error": "#FFFFFF",
    "error-container": "#FFDAD6",
    "on-error-container": "#410002",
    
    warning: "#FB8C00",
    "on-warning": "#FFFFFF",
    "warning-container": "#FFDBCC",
    "on-warning-container": "#2A1800",
    
    info: "#00CAE3",
    "on-info": "#FFFFFF",
    "info-container": "#C0F8FF",
    "on-info-container": "#001B20",
    
    success: "#4CAF50",
    "on-success": "#FFFFFF",
    "success-container": "#D8F5D9",
    "on-success-container": "#0A1F0C",
    
    // Surface colors
    surface: "#FEFBFF",
    "on-surface": "#1C1B1F",
    "surface-variant": "#E7E0EC",
    "on-surface-variant": "#49454F",
    "surface-container": "#F3F3F3",
    "surface-container-high": "#ECE6F0",
    "surface-container-highest": "#E6E1E5",
    
    // Background
    background: "#FEFBFF",
    "on-background": "#1C1B1F",
    
    // Outline
    outline: "#79747E",
    "outline-variant": "#CAC4D0",
    
    // Custom RCA colors (keeping existing business logic colors)
    accent: "#e91e63",
    lowRisk: "#FFDB00",
    medRisk: "#FFA524",
    highRisk: "#E00751",
    newCase: "#D7EBFC",
    underInvestigation: "#F3E0FF",
    closed: "#D2F2D2",
    defaulttext: "#484848",

    // For general use
    rcaDefaultBlue: "#004F93",

    // For use with 'RiskStatus'
    rcaStatusBlue: "#D7EBFC", // ASSIGNED
    rcaStatusGreen: "#D2F2D2", // CLOSED
    rcaStatusPink: "#F3E0FF", // INVESTIGATING
    rcaStatusGray: "#DFDFDF", // NEW
    rcaStatusRed: "#E00751", // OVERDUE
    rcaStatusYellow: "#FFD291", // IN REVIEW

    // For use with 'RiskLevel'
    rcaRiskRed: "#CC0008", // HIGH
    rcaRiskOrange: "#FFA524", // MEDIUM
    rcaRiskYellow: "#FFDB00" // LOW
  }
};

const darkTheme = {
  dark: true,
  colors: {
    // Material Design 3 dark theme
    primary: "#AAC7FF",
    "on-primary": "#002E5C",
    "primary-container": "#004080",
    "on-primary-container": "#D7EBFC",
    
    secondary: "#E6C200",
    "on-secondary": "#3C2F00",
    "secondary-container": "#564400",
    "on-secondary-container": "#FFF4C4",
    
    tertiary: "#F48FB1",
    "on-tertiary": "#5D1136",
    "tertiary-container": "#7D2449",
    "on-tertiary-container": "#F8BBD0",
    
    // System colors
    error: "#FFB4AB",
    "on-error": "#690005",
    "error-container": "#93000A",
    "on-error-container": "#FFDAD6",
    
    warning: "#FFB783",
    "on-warning": "#4A2800",
    "warning-container": "#6A3A00",
    "on-warning-container": "#FFDBCC",
    
    info: "#5FD4F4",
    "on-info": "#003640",
    "info-container": "#004D5C",
    "on-info-container": "#C0F8FF",
    
    success: "#7DD87F",
    "on-success": "#0A3A0E",
    "success-container": "#225526",
    "on-success-container": "#D8F5D9",
    
    // Surface colors
    surface: "#101013",
    "on-surface": "#E6E1E5",
    "surface-variant": "#49454F",
    "on-surface-variant": "#CAC4D0",
    "surface-container": "#1D1B20",
    "surface-container-high": "#272529",
    "surface-container-highest": "#322F35",
    
    // Background
    background: "#101013",
    "on-background": "#E6E1E5",
    
    // Outline
    outline: "#938F99",
    "outline-variant": "#49454F",
    
    // Custom RCA colors (adapted for dark theme)
    accent: "#F48FB1",
    lowRisk: "#FFDB00",
    medRisk: "#FFA524",
    highRisk: "#FFB4AB",
    newCase: "#2A4A6B",
    underInvestigation: "#4A3B5C",
    closed: "#2A4A2D",
    defaulttext: "#E6E1E5",

    // For general use
    rcaDefaultBlue: "#AAC7FF",

    // For use with 'RiskStatus' (adapted for dark theme)
    rcaStatusBlue: "#2A4A6B", // ASSIGNED
    rcaStatusGreen: "#2A4A2D", // CLOSED
    rcaStatusPink: "#4A3B5C", // INVESTIGATING
    rcaStatusGray: "#3C3C3C", // NEW
    rcaStatusRed: "#5C1A1A", // OVERDUE
    rcaStatusYellow: "#5C4A2A", // IN REVIEW

    // For use with 'RiskLevel'
    rcaRiskRed: "#FFB4AB", // HIGH
    rcaRiskOrange: "#FFB783", // MEDIUM
    rcaRiskYellow: "#E6C200" // LOW
  }
};

export default createVuetify({
  components,
  directives,
  icons: {
    defaultSet: "mdi",
    aliases,
    sets: {
      mdi,
    },
  },
  theme: {
    defaultTheme: 'light',
    variations: {
      colors: ['primary', 'secondary', 'tertiary'],
      lighten: 4,
      darken: 4,
    },
    themes: {
      light: lightTheme,
      dark: darkTheme
    }
  },
  defaults: {
    global: {
      ripple: false,
    },
    VBtn: {
      style: [{ fontWeight: 400, letterSpacing: 'normal' }],
      size: 'default',
      variant: 'elevated'
    },
    VCard: {
      variant: 'elevated'
    },
    VSheet: {
      variant: 'elevated'
    },
    VAppBar: {
      VBtn: {
        variant: 'text'
      }
    }
  },
  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});
